import { AreaId, ConfigurationValues } from '@cutr/constants/windows/windows';
import {
  BasicValue,
  CONFIGURATIONS_OPTIONS,
} from '@cutr/constants/windows/windows-configurations.ts';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import i18n from '@/i18n';
import { Label } from '@/windows/primitives/Label';
import { Select, SelectItem } from '@/windows/primitives/Select';

import { usePositionStore } from '../../../api/positionStore';
import { unboxLabel } from '../Configurations';
import { useConfigurations } from '../ConfigurationsContext';
import { LocalGridSelectModal } from '../inputs/LocalGridSelectModal';
import { ConfigurationComponent } from '.';

type SashbarsValue = NonNullable<ConfigurationValues['sashbars']>;

const SASHBARS_PROFILE_OPTIONS =
  CONFIGURATIONS_OPTIONS.sashbars.profile.options;
const SASHBARS_LAYOUT_OPTIONS = CONFIGURATIONS_OPTIONS.sashbars.layout.options;

const LAYOUT_OPTIONS_WITHOUT_IMAGE = new Set(['special-division']);

const layoutGridOptions = SASHBARS_LAYOUT_OPTIONS.map((config) => ({
  config,
  imagePath: LAYOUT_OPTIONS_WITHOUT_IMAGE.has(config.value)
    ? '/sorpetaler/none.jpg'
    : `/sorpetaler/sashbars/${config.value}.svg`,
}));

type SashbarsFormProps = {
  value: SashbarsValue | undefined;
  onChange: (next: SashbarsValue) => void;
};

const SashbarsForm = ({ value, onChange }: SashbarsFormProps) => {
  const { t } = useTranslation();
  const sashbarsLabelId = useId();
  const sashbarsInputId = useId();
  const layoutLabelId = useId();

  const current = value?.[0];
  const { profile, layout } = current || {
    profile: SASHBARS_PROFILE_OPTIONS[0],
    layout: SASHBARS_LAYOUT_OPTIONS[0],
  };

  return (
    <>
      <Label id={sashbarsLabelId} htmlFor={sashbarsInputId}>
        {t('windows.configurations.sashbars.profile.label')}
      </Label>
      <Select
        aria-labelledby={sashbarsLabelId}
        id={sashbarsInputId}
        items={SASHBARS_PROFILE_OPTIONS}
        selectedKey={profile.value}
        onSelectionChange={(selected) => {
          const nextProfile = SASHBARS_PROFILE_OPTIONS.find(
            (option) => option.value === selected
          );
          if (nextProfile) onChange([{ profile: nextProfile, layout }]);
        }}
      >
        {(option) => (
          <SelectItem
            id={option.value.toString()}
            textValue={unboxLabel(option.label)}
          >
            <span>{unboxLabel(option.label)}</span>
          </SelectItem>
        )}
      </Select>

      <Label id={layoutLabelId}>
        {t('windows.configurations.sashbars.layout.label')}
      </Label>
      <LocalGridSelectModal
        columnsCount={4}
        modalTitle={t('windows.configurations.sashbars.layout.modal.title')}
        modalDescription={t(
          'windows.configurations.sashbars.layout.modal.description'
        )}
        options={layoutGridOptions}
        value={layout}
        onChange={(nextLayout: BasicValue) =>
          onChange([{ profile, layout: nextLayout }])
        }
        showSelectedImage={true}
      />
    </>
  );
};

const SashbarsInput = () => {
  const { configurations, dispatch } = useConfigurations();

  return (
    <SashbarsForm
      value={configurations.sashbars}
      onChange={(next) => dispatch({ type: 'SET_SASHBARS', value: next })}
    />
  );
};

export const AreaSashbarsInput = ({ areaId }: { areaId: AreaId }) => {
  const { windowSpec, setAreaSashbars } = usePositionStore();
  const sashbars = windowSpec?.areaConfigurations[areaId]?.sashbars;

  return (
    <SashbarsForm
      value={sashbars}
      onChange={(next) => setAreaSashbars(areaId, next)}
    />
  );
};

const config: ConfigurationComponent<'sashbars'> = {
  key: 'sashbars',
  input: <SashbarsInput />,
  display: (value: ConfigurationValues['sashbars']) => {
    if (!value || value.length === 0) return '-';
    const profile = unboxLabel(value[0].profile?.label);
    const layout = unboxLabel(value[0].layout?.label);
    return (
      <div>
        <div>
          <span>
            {i18n.t('windows.configurations.sashbars.profile.label')}:{' '}
          </span>
          <span>{profile}</span>
        </div>
        <div>
          <span>
            {i18n.t('windows.configurations.sashbars.layout.label')}:{' '}
          </span>
          <span>{layout}</span>
        </div>
      </div>
    );
  },
};

export default config;
